import { CustomHeader } from "@/components/CustomHeader";
import { ThemedText } from "@/components/ThemedText";
import { ThemedView } from "@/components/ThemedView";
import { APP_HOME_ROUTE } from "@/constants/AppNavigation";
import Constants from "expo-constants";
import { useLocalSearchParams, useRouter } from "expo-router";
import Head from "expo-router/head";
import React from "react";
import { useTranslation } from "react-i18next";
import { StyleSheet, TouchableOpacity } from "react-native";

const SettingsPage = () => {
  const router = useRouter();
  const { t } = useTranslation();
  const params = useLocalSearchParams<{ plateTour?: string; step?: string }>();
  const isSettingsTourStep =
    params.plateTour === "1" && (params.step || "settings") === "settings";
  const version = Constants.expoConfig?.version ?? "0.0.0";
  const buildSha =
    (Constants.expoConfig?.extra?.buildSha as string | undefined) ?? "dev";

  const handleOpenPlates = () => {
    if (isSettingsTourStep) {
      router.replace(
        "/(tabs)/settings-plates?plateTour=1&step=plates" as never,
      );
      return;
    }

    router.push("/settings-plates" as never);
  };

  return (
    <>
      <Head>
        <title>{t("PAGE_TITLE_SETTINGS")}</title>
      </Head>
      <CustomHeader
        title={t("SETTINGS")}
        type="subtitle"
        variant="tab"
        showBackButton
        onBackPress={() => router.replace(APP_HOME_ROUTE as never)}
      />
      <ThemedView style={styles.container}>
        <TouchableOpacity
          style={styles.listItem}
          onPress={() => router.push("/settings-default-home" as never)}
        >
          <ThemedText style={styles.listItemTitle}>
            {t("DEFAULT_HOME")}
          </ThemedText>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.listItem, isSettingsTourStep && styles.tourHighlight]}
          onPress={handleOpenPlates}
        >
          <ThemedText style={styles.listItemTitle}>{t("PLATES")}</ThemedText>
        </TouchableOpacity>
        <TouchableOpacity
          style={styles.listItem}
          onPress={() => router.push("/settings-notifications" as never)}
        >
          <ThemedText style={styles.listItemTitle}>
            {t("NOTIFICATIONS")}
          </ThemedText>
        </TouchableOpacity>
        <TouchableOpacity
          style={styles.listItem}
          onPress={() => router.push("/settings-signin-methods" as never)}
        >
          <ThemedText style={styles.listItemTitle}>
            {t("SIGN_IN_METHODS")}
          </ThemedText>
        </TouchableOpacity>

        <ThemedText style={styles.versionText}>
          {t("APP_VERSION", { version, buildSha })}
        </ThemedText>

        {isSettingsTourStep && (
          <>
            <ThemedView style={styles.tourBackdrop} />
            <ThemedView style={styles.tourTooltip}>
              <ThemedText style={styles.tourTooltipTitle}>
                {t("PLATE_TOUR_STEP1_TITLE")}
              </ThemedText>
              <ThemedText style={styles.tourTooltipText}>
                {t("PLATE_TOUR_STEP1_MESSAGE")}
              </ThemedText>
              <TouchableOpacity onPress={handleOpenPlates}>
                <ThemedText type="link">{t("ONBOARDING_CONTINUE")}</ThemedText>
              </TouchableOpacity>
            </ThemedView>
          </>
        )}
      </ThemedView>
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
    alignSelf: "center",
    width: "100%",
    maxWidth: 600,
  },
  listItem: {
    position: "relative",
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: "#e5e7eb",
  },
  tourHighlight: {
    borderWidth: 2,
    borderColor: "#0a7ea4",
    borderRadius: 10,
    paddingHorizontal: 12,
    backgroundColor: "rgba(255,255,255,0.98)",
    shadowColor: "#0a7ea4",
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.9,
    shadowRadius: 12,
    zIndex: 3,
  },
  listItemTitle: {
    fontSize: 16,
    fontWeight: "600",
  },
  versionText: {
    marginTop: "auto",
    paddingTop: 24,
    fontSize: 12,
    lineHeight: 16,
    textAlign: "center",
    color: "#6B7280",
  },
  tourBackdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: "rgba(0, 0, 0, 0.45)",
    zIndex: 1,
  },
  tourTooltip: {
    position: "absolute",
    top: 170,
    right: 20,
    left: 20,
    borderRadius: 10,
    backgroundColor: "#fff",
    padding: 12,
    gap: 8,
    zIndex: 4,
    borderWidth: 1,
    borderColor: "#d9d9d9",
  },
  tourTooltipTitle: {
    fontSize: 15,
    fontWeight: "700",
  },
  tourTooltipText: {
    fontSize: 14,
  },
});

export default SettingsPage;
